/* ==========================================================================
   Linh vật Apus Ami — chim én có mắt chớp, cùng cách dựng với mascot web-marketing (`@apus/ami`):
   ba lớp ảnh trùng khung (thân · cánh · mắt nhắm), mắt nhắm hiện lên ~105 ms là một lần chớp.
   Nhịp chớp chép từ `useNaturalBlink`: lần đầu sau 1,0–1,3 s, sau đó mỗi 3,5–6,5 s, 18 % là chớp đôi;
   tab ẩn ⇒ dừng; `prefers-reduced-motion` ⇒ không chớp, không thở.
   Ảnh: assets/ami/pet/*.webp (thu từ apus-ami-widget/assets/pet, xem assets/ami/SOURCE.md).
   ========================================================================== */
import { el } from "./dom.ts";

/** `…/assets/ami/ami-head.webp` ⇒ `…/assets/ami/pet/`; URL khác (host tự đặt ảnh) ⇒ null = dùng ảnh tĩnh. */
export function petBase(headUrl: string): string | null {
  const m = /^(https?:\/\/.+\/)ami-head\.webp(?:[?#].*)?$/i.exec(headUrl);
  return m ? m[1] + "pet/" : null;
}

/**
 * Một khuôn mặt Ami. `full` = cả dáng đứng (có cánh, cho hình lớn); mặc định = cắt cận đầu (ô tròn nhỏ).
 * Thiếu ảnh lớp nào thì gỡ lớp đó — thân hỏng thì cả khối ẩn để chỗ chứa hiện biểu tượng dự phòng.
 */
export function amiFace(base: string, cls: string, opts: { full?: boolean } = {}): HTMLElement {
  const face = el("span", "apx-ami " + cls + (opts.full ? " apx-ami-full" : " apx-ami-head"), { "aria-hidden": "true" });
  const layer = (name: string, kind: string) => {
    const i = el("img", "apx-ami-layer apx-ami-" + kind, { src: base + name + ".webp", alt: "", draggable: "false" });
    i.addEventListener("error", () => (kind === "body" ? face.remove() : i.remove()));
    return i;
  };
  face.append(layer("body", "body"));
  if (opts.full) face.append(layer("wing", "wing"));
  face.append(layer("eyes-closed", "eyes"));
  return face;
}

/** Một bộ hẹn giờ chớp mắt cho MỌI khuôn mặt Ami trong `root` (chớp cùng nhịp — chỉ có một Ami). */
export class Blinker {
  private timers: number[] = [];
  private stopped = false;
  private readonly onVis = () => this.restart();
  private readonly root: ParentNode;

  /* ⛔ `constructor(private readonly root…)`: Node chạy test bằng lược kiểu (type stripping), không hiểu tham số-thuộc-tính. */
  constructor(root: ParentNode) {
    this.root = root;
    if (typeof window === "undefined") return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    document.addEventListener("visibilitychange", this.onVis);
    this.schedule(1000 + Math.random() * 300);
  }

  private set(on: boolean): void {
    this.root.querySelectorAll<HTMLElement>(".apx-ami, .apx-pet-rig").forEach((f) => {
      if (on) f.setAttribute("data-blinking", "true");
      else f.removeAttribute("data-blinking");
    });
  }

  private later(fn: () => void, ms: number): void {
    this.timers.push(window.setTimeout(fn, ms));
  }

  private schedule(delay = 3500 + Math.random() * 3000): void {
    if (this.stopped || document.visibilityState === "hidden") return;
    this.later(() => {
      const double = Math.random() < 0.18;
      this.set(true);
      this.later(() => {
        this.set(false);
        if (!double) return this.schedule();
        this.later(() => {
          this.set(true);
          this.later(() => {
            this.set(false);
            this.schedule();
          }, 105);
        }, 145);
      }, 105);
    }, delay);
  }

  private clear(): void {
    this.timers.forEach((t) => window.clearTimeout(t));
    this.timers = [];
    this.set(false);
  }

  /** Tab ẩn ⇒ dừng hẳn; hiện lại ⇒ mở mắt, chờ một nhịp ngắn rồi chớp tiếp. */
  private restart(): void {
    this.clear();
    if (document.visibilityState !== "hidden") this.schedule(1000 + Math.random() * 300);
  }

  stop(): void {
    this.stopped = true;
    this.clear();
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", this.onVis);
  }
}
